/**
 * API Client
 *
 * Centralized HTTP client with interceptors for auth, error handling, etc.
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import envConfig from "@/config/env";
import { getOrgName } from "@/config/organization";
import { trackRumError } from "@/lib/datadog";
import { redirectToLogin, SESSION_EXPIRED_MESSAGE } from "@/lib/auth";

export { redirectToLogin, SESSION_EXPIRED_MESSAGE };

// API Error type
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
  detail?: string;
}

/**
 * Runtime narrowing for caught exceptions on api / authApi calls. Our
 * axios response interceptor reshapes every rejection into an ApiError,
 * but TS doesn't know that at the call site — use this to avoid `as any`
 * casts when inspecting `.status` / `.message` in catch blocks.
 */
export function isApiError(err: unknown): err is ApiError {
  return (
    typeof err === "object" &&
    err !== null &&
    typeof (err as { message?: unknown }).message === "string"
  );
}

// Create axios instance. ``withCredentials: true`` is mandatory now — the
// backend issues HttpOnly cookies (si_at, si_rt) that the browser must
// attach on every cross-origin request. Without this flag the browser
// silently drops the cookies on cross-subdomain calls (e.g. SPA on
// dlc.epr.urbint.com → API on api.urbint.com) and every request 401s.
const apiClient: AxiosInstance = axios.create({
  baseURL: envConfig.apiBaseUrl,
  timeout: envConfig.apiTimeout,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - add org-name header
apiClient.interceptors.request.use(
  (config) => {
    // Add organization name header
    config.headers["x-org-name"] = getOrgName();

    // Log requests in development
    if (envConfig.isDev) {
      console.log(
        `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
        {
          params: config.params,
          headers: {
            "x-org-name": config.headers["x-org-name"],
          },
        },
      );
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle errors uniformly
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log responses in development
    if (envConfig.isDev) {
      console.log(
        `[API Response] ${response.config.url}`,
        response.status,
        response.data,
      );
    }
    return response;
  },
  (error: AxiosError<ApiError>) => {
    // Transform error to consistent format
    const apiError: ApiError = {
      message:
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.message ||
        "An unexpected error occurred",
      code: error.response?.data?.code || error.code,
      status: error.response?.status,
      details: error.response?.data?.details,
    };

    if (envConfig.isDev) {
      console.error("[API Error]", apiError);
    }

    const status = error.response?.status;
    // Cancelled requests are normal control flow (React Query aborts superseded
    // queries) — they are not failures worth reporting.
    const isCanceled =
      error.code === "ERR_CANCELED" || error.name === "CanceledError";
    if (!isCanceled && status !== 401 && status !== 403) {
      trackRumError(error, {
        url: error.config?.url,
        method: error.config?.method,
        status,
        message: apiError.message,
      });
    }

    // 401 = session expired / unauthenticated → redirect to login.
    // 403 = authenticated but not authorized → let it propagate so the
    //       caller can render an "Access denied" state. Never send a
    //       logged-in user back to /login just because they lack a role.
    // In mock mode (MSW) we never redirect — mock auto-login owns auth.
    if (status === 401 && !envConfig.enableMockApi) {
      redirectToLogin(SESSION_EXPIRED_MESSAGE);
    }

    return Promise.reject(apiError);
  },
);

// Helper methods for cleaner API calls
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((res) => res.data),
};

// Export the raw client for advanced use cases
export { apiClient };
export default api;
